import type { FC, ReactNode } from 'react';
import { Link } from 'react-router';

import { copyrightStyle } from '@/features/storefront/appearance';
import { StorefrontContainer } from '@/features/storefront/components/StorefrontContainer';
import { useShopContent } from '@/features/storefront/content/useContent';
import { LanguageSwitcherSlot } from '@/features/storefront/i18n/LanguageSwitcher';
import { AppStoreBadges } from '@/features/storefront/layouts/chrome/FooterExtras';
import { InstallAppSlot } from '@/features/storefront/pwa/InstallAppSlot';
import { useCategoryPath, useShelfCategories } from '@/features/storefront/hooks/useShelfCategories';
import {
  BlogLinks,
  MenuLink,
  PageLinks,
} from '@/features/storefront/layouts/chrome/ChromeParts';
import {
  FOOTER_CATEGORY_LIMIT,
  categoryColumns,
  menuItems,
  type FooterProps,
} from '@/features/storefront/layouts/chrome/types';
import { cn } from '@/lib/cn';

/**
 * Serra's footer grid, by how many link columns stand beside the wordmark.
 * Written out rather than assembled from parts, because Tailwind reads these
 * class names out of the source and never sees a string built at runtime.
 */
const SERRA_FOOTER_GRID: Record<number, string> = {
  0: '',
  1: 'lg:grid-cols-[1.4fr_1fr]',
  2: 'lg:grid-cols-[1.4fr_1fr_1fr]',
  3: 'lg:grid-cols-[1.4fr_1fr_1fr_1fr]',
  4: 'lg:grid-cols-[1.4fr_1fr_1fr_1fr_1fr]',
};

/**
 * One Serra footer column: tracked small caps over the links beneath them.
 *
 * A column continuing the one before it takes no heading of its own, only the
 * space one would have used, so a category list split in two still has its
 * links level with the columns either side of it.
 */
function SerraColumn({ title, children }: { title?: string; children: ReactNode }) {
  return (
    <div>
      <p
        className="mb-3.5 text-[11px] font-medium uppercase tracking-[0.14em]"
        style={{ color: 'var(--storefront-text)' }}
        aria-hidden={title ? undefined : true}
      >
        {title ?? '\u00a0'}
      </p>
      {children}
    </div>
  );
}

/**
 * The house's colophon: a serif wordmark and its address over columns of
 * tracked small caps, closed by a hairline and the fine print.
 *
 * The columns are the merchant's footer menu, the shop's own shelves, and its
 * published pages and blogs - each skipped when there is nothing to put in it,
 * and the grid sized to however many are left, so a Serra shop is never given
 * a footer of headings with nothing under them, nor one lone column stranded
 * against the wordmark.
 */
export const Footer: FC<FooterProps> = ({ storeName, footer, description, menu, preview }) => {
  const items = menuItems(menu);
  const shelfColumns = categoryColumns(
    useShelfCategories({ limit: FOOTER_CATEGORY_LIMIT, enabled: !preview }),
  );
  const categoryPath = useCategoryPath();
  const copy = useShopContent();
  const columns = (items.length > 0 ? 1 : 0) + shelfColumns.length + (preview ? 0 : 1);
  // The merchant may well have called their own menu "Shop" already, and two
  // columns under one heading read as a single list broken in half. Compared
  // against the shop's own word for the column, in the shopper's language.
  const shopTitle = copy('theme.footer_shop');
  const shelvesTitle =
    menu?.name.trim().toLowerCase() === shopTitle.trim().toLowerCase()
      ? copy('header.menu_categories')
      : shopTitle;

  return (
    <footer
      className="border-t py-14 text-sm"
      style={{ borderColor: 'var(--storefront-surface)', color: 'var(--storefront-muted)' }}
    >
      <StorefrontContainer
        className={cn('grid gap-10 text-start sm:grid-cols-2', SERRA_FOOTER_GRID[columns])}
      >
        <div>
          <p
            className="text-xl uppercase tracking-[0.1em]"
            style={{
              color: 'var(--storefront-text)',
              fontFamily: 'var(--storefront-heading-font)',
            }}
          >
            {storeName}
          </p>
          {description ? <p className="mt-3.5 max-w-[16rem] leading-relaxed">{description}</p> : null}
        </div>
        {items.length > 0 ? (
          <SerraColumn title={menu?.name ?? shopTitle}>
            <nav aria-label={menu?.name} className="flex flex-col items-start gap-2.5">
              {items.map((item) => (
                <MenuLink
                  key={`${item.label}:${item.url}`}
                  item={item}
                  preview={preview}
                  className="hover:opacity-60"
                />
              ))}
            </nav>
          </SerraColumn>
        ) : null}
        {shelfColumns.map((column, index) => (
          <SerraColumn key={column[0].id} title={index === 0 ? shelvesTitle : undefined}>
            <nav
              aria-label={
                index === 0 ? shelvesTitle : copy('theme.footer_continued', { title: shelvesTitle })
              }
              className="flex flex-col items-start gap-2.5"
            >
              {column.map((category) => (
                <Link key={category.id} to={categoryPath(category.slug)} className="hover:opacity-60">
                  {category.name}
                </Link>
              ))}
            </nav>
          </SerraColumn>
        ))}
        {preview ? null : (
          <SerraColumn title={copy('theme.footer_client_care')}>
            <PageLinks className="mb-2 flex-col items-start justify-start gap-2.5" />
            <BlogLinks className="flex-col items-start justify-start gap-2.5" />
            <InstallAppSlot className="mt-4" />
            <AppStoreBadges footer={footer} preview={preview} className="mt-4" />
          </SerraColumn>
        )}
      </StorefrontContainer>
      <StorefrontContainer className="mt-11">
        <div
          className="flex flex-wrap justify-between gap-x-6 gap-y-2 border-t pt-5 text-[11px]"
          style={{ borderColor: 'var(--storefront-surface)' }}
        >
          <LanguageSwitcherSlot placement="footer" preview={preview} />
          <span style={copyrightStyle(footer)}>
            © {new Date().getFullYear()} {storeName}
          </span>
          <span>
            {copy('theme.prices_inr_gst')} · {copy('theme.powered_by', { brand: 'NeonCarts' })}
          </span>
        </div>
      </StorefrontContainer>
    </footer>
  );
};
